/* eslint-disable consistent-return */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem, SubMenu } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Text from '../../../../shared/components/Text/Text';
import SuperSetPickerPopup from './SuperSetPickerPopup';
import PacePickerPopup from './PacePickerPopup';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setActiveModal,
  revertSwappedItemToParent,
  updateCalendarItemsOrder,
  updateWorkoutItemsOrder,
  setCombineWithInfo,
  setCalendarItems,
  setNonDateBasedProgramItems,
  setRecurringCalendarItems,
  setIsCalendarOptionsDropdownMenuOpen,
  setSwapDropdownToggleEnabled,
} from '../../ducks/calendarActions';
import { cancelDebounceForTable, flushDebounceForTable } from './hooks/debounceHandler';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;

`;

const DropdownContainer = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  justify-content: center;
  padding: 7px;
  border-radius: 4px;
  cursor: pointer;
  :hover {
   background: rgba(119, 167, 255, 0.2);
  }

  .AdditionalOptionsDots {
    fill: rgba(189, 189, 189, 1);
  }

  &:hover .AdditionalOptionsDots  {
    fill: #77A7FF;
  }
`;

const MenuWrapper = styled('div')`
  .custom-rc-menu {
    border-radius: 4px !important;
    box-shadow: 0px 4px 8px 0px #3031331A !important;
    border: 1px solid #EEEEEE !important;

    padding-top: 8px;
    padding-bottom: 8px;
    
    /* Hide the default scrollbar track */
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    /* Style the scrollbar width */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    /* Style the scrollbar thumb */
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
    
    /* For Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }

  .rc-menu-submenu-title {
    padding: 0px !important;
  }

  .rc-menu-submenu-arrow {
    display: none;
  }

`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 0;
  background-color: #EEEEEE;

  margin-left: 18px;
  margin-right: 18px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const RedDashThrough = styled.div`
  width: 24px;
  height: 2px;
  background: #BDBDBD;
  position: absolute;
  transform: rotate(45deg);
  transform-origin: center;
  left: -3px;
  top: 10px;
`;

const ParentIconWrapper = styled('div')`
  height: 16px;
  width: 16px;
  background: #B5B5B5;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ParentIconBorderBox = styled('div')`
  border: 1px solid white;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  width: 12px;
  text-align: center;
`;

const VisibleClickDiv = styled('div')`

`;

// eslint-disable-next-line react/prop-types
const HoverableMenuItem = ({
  hoverableItem,
  content,
  isRecurring,
  assignedPace,
  setAssignedPace,
  effectiveDate,
  dayNumber,
  measure,
}) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const calendarContainerScrollPosition = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPosition,
  );

  useEffect(() => {
    /** close select menu if main container is scrolled */
    if (subMenuVisible) {
      setSubMenuVisible(false);
    }
  }, [calendarContainerScrollPosition]);

  useEffect(() => {
    const handleResize = () => {
      if (subMenuVisible) {
        setSubMenuVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [subMenuVisible]);

  const handleOpenChange = (keys) => {
    // Only allow one submenu to be open at a time
    if (keys.length > 1) {
      setOpenKeys([keys[keys.length - 1]]); // Keep only the most recently opened
    } else {
      setOpenKeys(keys);
    }
  };

  const [isHovered, setIsHovered] = useState(false);

  const disabled = (hoverableItem.label === 'Completion Only' && ((content?.exercise?.type === 'R' && content?.exercise?.inputType === 'C') || (content?.type === 'R' && content?.preSave && content.inputType === 'C')));
  const menuItemContent = (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', position: 'relative',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: hoverableItem.shortcut === 'superset' ? '0px' : '8px', cursor: disabled && 'not-allowed',
      }}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={hoverableItem.shortcut === 'superset' ? 24 : 16}
          icon={hoverableItem.icon}
          color={disabled ? 'rgb(203, 201, 201)' : (hoverableItem.color || '#9E9E9E')}
        />
        {hoverableItem.extra}
        <Text
          fontSize='16px'
          color={disabled ? 'rgb(203, 201, 201)' : '#444444'}
        >
          {hoverableItem.label}
        </Text>
      </div>
      {hoverableItem.isChecked !== undefined && hoverableItem.isChecked && (
        <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color={disabled ? 'rgb(203, 201, 201)' : '#444444'} />
      )}
      {hoverableItem.shortcut && (

        hoverableItem.shortcut === 'superset' || hoverableItem.shortcut === 'pace'
          ? <IcomoonReact iconSet={iconSet} size={10} icon='altrightarrow' color='#444444' />
          : <Text color='#444444'>{hoverableItem.shortcut}</Text>

      )}
    </div>
  );

  if (hoverableItem.shortcut === 'superset' || hoverableItem.shortcut === 'pace') {
    return (
      <Menu
        triggerSubMenuAction='click'
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        style={{
          border: 'none',
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
        }}
        className='nested-submenu'
      >
        <VisibleClickDiv onClick={() => setSubMenuVisible(true)}>
          <SubMenu
            key={hoverableItem.key}
            title={(
              <div style={{
                paddingTop: 7,
                paddingBottom: 7,
                paddingLeft: 16,
                paddingRight: 16,
                cursor: hoverableItem.disabled ? 'not-allowed' : 'pointer',
                background: isHovered ? '#F5F5F5' : 'white',
              }}
              >
                {menuItemContent}
              </div>
        )}
            onTitleMouseEnter={() => setIsHovered(true)}
            onTitleMouseLeave={() => setIsHovered(false)}
            popupClassName={
            `${hoverableItem.shortcut === 'superset' ? 'custom-submenu-supersetPicker' : 'pace-dropdown'}${!subMenuVisible ? ' custom-submenu-supersetPicker-displayNone' : ''}`
            }
          >
            <MenuItem key={`${hoverableItem.key}-popup`} style={{ padding: 0 }}>
              {hoverableItem.shortcut === 'superset' ? (
                <SuperSetPickerPopup
                  content={content}
                  left={0}
                  top={0}
                  spaceAbove={null}
                  isRecurring={isRecurring}
                  effectiveDate={effectiveDate}
                  dayNumber={dayNumber}
                />
              ) : (
                <PacePickerPopup
                  left={0}
                  top={0}
                  spaceAbove={null}
                  assignedPace={assignedPace}
                  setAssignedPace={setAssignedPace}
                  measure={measure}
                />
              )}
            </MenuItem>
          </SubMenu>
        </VisibleClickDiv>
      </Menu>
    );
  }

  return (
    <MenuItem
      key={hoverableItem.key}
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 16,
        paddingRight: 16,
        cursor: hoverableItem.disabled ? 'not-allowed' : 'pointer',
        background: isHovered ? '#F5F5F5' : 'transparent',
      }}
      onClick={hoverableItem.disabled ? null : (e) => hoverableItem.action(e)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        {menuItemContent}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  hoverableItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    action: PropTypes.func,
    extra: PropTypes.node,
    isChecked: PropTypes.bool,
    shortcut: PropTypes.string,
  }).isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  setAssignedPace: PropTypes.func,
  effectiveDate: PropTypes.string.isRequired,
  dayNumber: PropTypes.number.isRequired,
  measure: PropTypes.func.isRequired,
};

HoverableMenuItem.defaultProps = {
  isRecurring: '',
  assignedPace: '',
  setAssignedPace: () => {},
};

const CalendarItemDropdownOptions = ({
  setCoachCompletion,
  coachCompletion,
  setCompletionOnly,
  completionOnly,
  isLeftAndRight,
  setIsLeftAndRight,
  content,
  index,
  trackVolumeLoad,
  setTrackVolumeLoad,
  trackRepCount,
  setTrackRepCount,
  forcePrUpdate,
  setForcePrUpdate,
  disablePrUpdate,
  setDisablePrUpdate,
  isExpanded,
  setIsExpanded,
  expandId,
  iconOb,
  setIconOb,
  contentType,
  handleRemovePresaveItemFromRedux,
  localAdditionalInfo,
  isRecurring = '',
  assignedPace,
  setAssignedPace,
  isWeekView = false,
  effectiveDate = null,
  dayNumber = null,
  measure,
  // eslint-disable-next-line react/prop-types
  onDropdownChange,
  flags,
}) => {
  const [localDropdownVisible, setLocalDropdownVisible] = useState(false);
  const dispatch = useDispatch();
  const [maxDropdownHeight, setMaxDropdownHeight] = useState(500);
  const dropdownRef = useRef(null);
  const allExercises = useSelector((state) => state.calendar.data.allExercises);
  const programType = useSelector((state) => state.calendar.data.programType);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarContainerScrollPosition = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPosition,
  );
  const date = new Date(effectiveDate || selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const calendarItemsForSelectedDate = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      dayNumber || selectedProgramDayNumber
    ]?.exercises || []) : useSelector(
      (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.exercises || [],
    );
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts[endpointFormattedDate] || [],
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts[endpointFormattedDate] || [],
  );

  const defaultExOptions = content?.preSave
    ? content?.defaultOptions
    : content?.exercise?.defaultOptions;

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (localDropdownVisible) {
      dispatch(setIsCalendarOptionsDropdownMenuOpen(true));
    } else {
      dispatch(setIsCalendarOptionsDropdownMenuOpen(false));
    }
    const handleResize = () => {
      if (localDropdownVisible) {
        setLocalDropdownVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [localDropdownVisible]);

  useEffect(() => {
    if (localDropdownVisible) {
      setLocalDropdownVisible(false);
    }
  }, [calendarContainerScrollPosition]);

  const shouldAddInfoShow = (paramContent) => {
    if (paramContent?.type === 'warm_up' || paramContent?.type === 'session_break' || contentType === 'video' || content?.type === 'video' || contentType === 'CD' || paramContent?.type === 'cooldown' || paramContent?.type === 'note' || paramContent?.type === 'health' || paramContent?.type === 'progression' || paramContent?.type === 'P' || paramContent?.type === 'rest') {
      return false;
    }
    return true;
  };

  const isNonDuplicableType = ['session_break', 'rest', 'video', 'P'].includes(content.type)
        || content?.exercise?.type === 'P';

  const menuItems = [
    {
      key: '1',
      label: 'View Item Info',
      icon: 'preview',
      action: () => {
        const contentExercise = allExercises.find(
          (exercise) => exercise?.id === content?.exercise?.id,
        );
        dispatch(setActiveModal('createNewComponentModal', {
          type: content?.preSave ? content?.type : content?.exercise.type,
          editModal: true,
          disabledEditModal: true,
          itemInfoForEditModal: content?.preSave ? content : contentExercise,
          isPreviewOnly: true,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: contentType === 'session_break' || content?.type === 'rest' || contentType === 'H' || content?.type === 'video' || content?.type === 'progression' || content.type === 'P' || content.type === 'T' || content.type === 'tag',
    },
    {
      key: '1a',
      label: isExpanded ? 'Collapse' : 'Expand',
      icon: 'collapse',
      action: () => setIsExpanded(expandId),
      inActive: false,
    },
    {
      key: '2',
      label: 'Copy',
      icon: 'duplicate',
      action: () => {},
      inActive: true,
    },
    {
      key: '3',
      label: 'Swap',
      icon: 'swap-alt',
      action: () => {
        const newIconOb = { ...iconOb };
        newIconOb.swapDropdownToggle.enabled = true;
        dispatch(setSwapDropdownToggleEnabled(content?.assignedId));
        setIconOb(newIconOb);
        if (!isExpanded) {
          setIsExpanded(true);
        }
        setLocalDropdownVisible(false);
      },
      inActive: isRecurring || isNonDuplicableType,
    },
    {
      key: '3b',
      label: 'Revert to Parent Item',
      icon: '', // Empty icon to hide the default icomoon icon
      extra: (
        <ParentIconWrapper>
          <ParentIconBorderBox>
            <Text fontSize={10} style={{ marginTop: 2 }} color='white'>
              P
            </Text>
          </ParentIconBorderBox>
        </ParentIconWrapper>
      ),
      action: () => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const assignedId = content?.assignedId;
        cancelDebounceForTable(assignedId);
        dispatch(revertSwappedItemToParent(accountCode, assignedId, index, formattedDate));
        setLocalDropdownVisible(false);
      },
      inActive: !content?.swap,
    },
    {
      key: '4',
      label: !isRecurring ? 'Set as Recurring' : 'Update Recurring',
      icon: 'recurring-circle',
      action: () => {
        dispatch(setActiveModal('setRecurringItemModal', {
          ...content, itemIndex: index, isRecurring, effectiveDate,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: programType === 'free-form-program' || content?.preSave || content?.exercise?.isDeleted || content?.tag?.isDeleted,
    },
    {
      key: '4-b',
      label: 'Update Future Occurrences',
      icon: 'double-checkmark',
      action: () => {
        const modalData = {
          content,
          index,
          date: moment(effectiveDate || selectedDate).format('YYYY-MM-DD'),
          assignedId: content.assignedId,
        };
        if (content.isInherited && content.calendarId) {
          modalData.isInherited = true;
          modalData.parentId = content.calendarId;
        }
        dispatch(setActiveModal('updateFutureOccurrencesModal', modalData));
        setLocalDropdownVisible(false);
      },
      inActive: !flags.ufo
        || isRecurring
        || content.preSave
        || content.type === 'video'
        || content.type === 'rest'
        || content.type === 'session_break'
        || programType === 'free-form-program',
    },
    { divider: true, inActive: false },
    {
      key: '4-a',
      label: `${(localAdditionalInfo || content?.additionalInfo) ? 'Edit' : 'Add'} Additional Information`,
      icon: 'note2',
      action: () => {
        flushDebounceForTable(content.assignedId);
        dispatch(setActiveModal('addAdditionalInfoModal', {
          ...content,
          itemIndex: index,
          effectiveDate,
          dayNumber,
          prevLocAddInfo: localAdditionalInfo,
        }));
        setLocalDropdownVisible(false);
      },
      inActive: !shouldAddInfoShow(content),
    },
    {
      divider: true,
      inActive: isRecurring
      || !shouldAddInfoShow(content) || (calendarItemsForSelectedDate?.length === 1),
    },
    {
      key: '5',
      label: 'Move Up',
      icon: 'alt-up-arrow',
      action: () => {
        if (!isRecurring && index !== 0 && calendarItemsForSelectedDate?.length !== 1) {
          const originalItems = [...calendarItemsForSelectedDate];
          const newItems = Array.from(calendarItemsForSelectedDate);
          const [removed] = newItems.splice(index, 1);
          newItems.splice(index - 1, 0, removed);
          const newOrderedAssignmentIds = [];
          newItems.forEach((calendarItem) => {
            if (typeof calendarItem.assignedId !== 'string') {
              newOrderedAssignmentIds.push(calendarItem.assignedId);
            }
          });

          if (programType === 'free-form-program') {
            const submissionObject = {
              dayNumber,
              order: newOrderedAssignmentIds,
            };

            /** we need to set items here before checking length
             *  in case all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            if (newOrderedAssignmentIds?.length) {
              dispatch(
                updateWorkoutItemsOrder(currentUser.accountCode, submissionObject, originalItems),
              );
            }
          } else {
            const submissionObject = {
              date: moment(effectiveDate).format('YYYY-MM-DD'),
              order: newOrderedAssignmentIds,
              assignedIdsBeingMoved: [removed.assignedId],
              movedToIndex: newOrderedAssignmentIds.indexOf(removed.assignedId),
            };
            /** we need to set items here before checking length
             * in case all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateCalendarItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
            }
          }
        }
      },
      inActive: isRecurring || index === 0 || calendarItemsForSelectedDate?.length === 1,
    },
    {
      key: '6',
      label: 'Move Down',
      icon: 'alt-down-arrow',
      action: () => {
        if (!isRecurring
          && calendarItemsForSelectedDate?.length > 1
          && index !== calendarItemsForSelectedDate?.length - 1) {
          const originalItems = [...calendarItemsForSelectedDate];
          const newItems = Array.from(calendarItemsForSelectedDate);
          const [removed] = newItems.splice(index, 1);
          newItems.splice(index + 1, 0, removed);

          const newOrderedAssignmentIds = [];
          newItems.forEach((calendarItem) => {
            if (typeof calendarItem.assignedId !== 'string') {
              newOrderedAssignmentIds.push(calendarItem.assignedId);
            }
          });

          if (programType === 'free-form-program') {
            const submissionObject = {
              dayNumber,
              order: newOrderedAssignmentIds,
            };
            /** we need to set items here as well incase all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateWorkoutItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
              dispatch(setNonDateBasedProgramItems(newItems, dayNumber));
            }
          } else {
            const submissionObject = {
              date: moment(effectiveDate).format('YYYY-MM-DD'),
              order: newOrderedAssignmentIds,
              assignedIdsBeingMoved: [removed.assignedId],
              movedToIndex: newOrderedAssignmentIds.indexOf(removed.assignedId),
            };
            /** we need to set items here as well incase all items are pre save items
             * which would track newOrderAssignmentIds as 0 lenght
             */
            dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            if (newOrderedAssignmentIds?.length) {
              dispatch(updateCalendarItemsOrder(
                currentUser.accountCode,
                submissionObject,
                originalItems,
              ));
              dispatch(setCalendarItems(newItems, moment(effectiveDate).format('YYYY-MM-DD')));
            }
          }
        }
      },
      inActive: isRecurring
      || index === (calendarItemsForSelectedDate.length ?? 0) - 1
      || calendarItemsForSelectedDate?.length === 1,
    },
    {
      divider: true,
      inActive: isRecurring
      || index === (calendarItemsForSelectedDate.length ?? 0) - 1
      || calendarItemsForSelectedDate?.length === 1,
    },
    {
      key: '14',
      label: content.groupingLetter ? 'Superset Label' : 'Superset',
      icon: 'superset',
      action: () => {},
      shortcut: 'superset',
      isChecked: false,
      inActive: contentType === 'session_break' || content?.type === 'questionnaire',
    },
    {
      key: '99',
      label: 'Remove Superset',
      icon: 'unlink',
      action: () => {
        /** create an new array of item that we can manipulate and set with
        * grouping letters and colors
        */
        let items;
        if (isRecurring === 'before') {
          items = [...beforeRecurringWorkouts];
        } else if (isRecurring === 'after') {
          items = [...afterRecurringWorkouts];
        } else {
          items = [...calendarItemsForSelectedDate];
        }

        /** we need to track what the original items values were */
        let originalCalendarItems;
        if (isRecurring === 'before') {
          originalCalendarItems = beforeRecurringWorkouts.map((item) => ({ ...item }));
        } else if (isRecurring === 'after') {
          originalCalendarItems = afterRecurringWorkouts.map((item) => ({ ...item }));
        } else {
          originalCalendarItems = calendarItemsForSelectedDate.map((item) => ({ ...item }));
        }

        /** original target */
        const originalTarget = originalCalendarItems.find(
          (item) => item.assignedId.toString() === content?.assignedId.toString(),
        );

        /** target to manipulate and mutate for setting in redux  */
        const targetItem = items.find(
          (item) => item.assignedId.toString() === originalTarget?.assignedId.toString(),

        );

        /** update/mutate these items in the array that we will set in redux */
        targetItem.groupingLetter = null;
        targetItem.groupingColorCode = null;

        if (isRecurring === 'before' || isRecurring === 'after') {
          const itemsTosend = isRecurring === 'before' ? beforeRecurringWorkouts : afterRecurringWorkouts;
          dispatch(setRecurringCalendarItems(itemsTosend, moment(effectiveDate).format('YYYY-MM-DD'), isRecurring));
        } else if (programType === 'free-form-program') {
          dispatch(setNonDateBasedProgramItems(items, dayNumber));
        } else {
          dispatch(setCalendarItems(items, moment(effectiveDate).format('YYYY-MM-DD')));
        }

        dispatch(setCombineWithInfo({
          /** if either the target item of the sourceItem grouping letter is the same
               * as the original then we don't need to set something to trigger an update
               */
          target: targetItem,
          source: null,
          isMultiSelect: false,
        }));
      },
      isChecked: false,
      inActive: !content.groupingLetter,
    },
    { divider: true, inActive: contentType === 'session_break' || content?.type === 'questionnaire' || content.type === 'health' },
    {
      key: '13',
      label: 'Each Side (Left & Right)',
      icon: 'each-side',
      action: () => {
        const newValue = !isLeftAndRight;
        const newIconOb = { ...iconOb };
        newIconOb.eachSideToggle.enabled = newValue;
        setIconOb(newIconOb);
        setIsLeftAndRight(newValue);
        if (onDropdownChange) {
          onDropdownChange({ isLeftAndRight: newValue });
        }
      },
      isChecked: isLeftAndRight,
      inActive: contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'N' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
    },
    { divider: true, inActive: contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'N' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P' },
    {
      key: '7',
      label: 'Track Volume Load',
      icon: 'pr-tracking',
      action: () => {
        const newValue = !trackVolumeLoad;
        const newIconOb = { ...iconOb };
        newIconOb.trackVolumeLoadToggle.enabled = newValue;
        setIconOb(newIconOb);
        setTrackVolumeLoad(newValue);
        if (onDropdownChange) {
          onDropdownChange({ trackVolumeLoad: newValue });
        }
      },
      isChecked: trackVolumeLoad,
      inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
    },
    {
      key: '8',
      label: 'Track Rep Count',
      icon: 'send',
      action: () => {
        const newValue = !trackRepCount;
        const newIconOb = { ...iconOb };
        newIconOb.trackRepCountToggle.enabled = newValue;
        setIconOb(newIconOb);
        setTrackRepCount(newValue);
        if (onDropdownChange) {
          onDropdownChange({ trackRepCount: newValue });
        }
      },
      isChecked: trackRepCount,
      inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
    },
    { divider: true, inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P' },
    {
      key: '9',
      label: 'Force PR Update',
      icon: 'force-pr-update',
      action: () => {
        const newForcePrUpdate = !forcePrUpdate;
        const newDisablePrUpdate = newForcePrUpdate ? false : disablePrUpdate;

        const newIconOb = { ...iconOb };
        newIconOb.forcePrUpdateToggle.enabled = newForcePrUpdate;
        newIconOb.disablePrUpdateToggle.enabled = newDisablePrUpdate;

        // If Force PR Update is currently off, turn it on and turn off Disable PR Update
        if (!forcePrUpdate) {
          newIconOb.forcePrUpdateToggle.enabled = true;
          setForcePrUpdate(true);

          // Turn off Disable PR Update if it's currently on
          if (disablePrUpdate) {
            newIconOb.disablePrUpdateToggle.enabled = false;
            setDisablePrUpdate(false);
          }
        } else {
          // If Force PR Update is currently on, just turn it off
          newIconOb.forcePrUpdateToggle.enabled = false;
          setForcePrUpdate(false);
        }

        setIconOb(newIconOb);
        setForcePrUpdate(newForcePrUpdate);
        setDisablePrUpdate(newDisablePrUpdate);
        if (onDropdownChange) {
          onDropdownChange(
            { forcePrUpdate: newForcePrUpdate, disablePrUpdate: newDisablePrUpdate },
          );
        }
      },
      isChecked: forcePrUpdate,
      inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
      disabled: false,
    },
    {
      key: '10',
      label: 'Disable PR Update',
      icon: 'force-pr-update',
      action: () => {
        const newDisablePrUpdate = !disablePrUpdate;
        const newForcePrUpdate = newDisablePrUpdate ? false : forcePrUpdate;

        const newIconOb = { ...iconOb };
        newIconOb.disablePrUpdateToggle.enabled = newDisablePrUpdate;
        newIconOb.forcePrUpdateToggle.enabled = newForcePrUpdate;

        setIconOb(newIconOb);
        setDisablePrUpdate(newDisablePrUpdate);
        setForcePrUpdate(newForcePrUpdate);
        if (onDropdownChange) {
          onDropdownChange(
            { disablePrUpdate: newDisablePrUpdate, forcePrUpdate: newForcePrUpdate },
          );
        }
      },
      isChecked: disablePrUpdate,
      extra: <RedDashThrough />,
      inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
      disabled: false,
    },
    { divider: true, inActive: contentType === 'N' || contentType === 'R' || contentType === 'C' || contentType === 'S' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P' },
    {
      key: '11',
      label: 'Completion Only',
      icon: 'completion-circle',
      action: () => {
        /** if the content type is SAQC and the original exericse
         * is inputType completion only, then we should NOT be able to toggle completion only
         * if the original exercise is not SAQC then we SHOULD be able to toggle
         */
        if ((contentType === 'R' && content?.exercise?.inputType === 'C') || (contentType === 'R' && content?.preSave && content.inputType === 'C')) {
          return;
        }
        /** if the content type is Circuits the user should never be able to toggle
         * completion only
         */
        if ((contentType === 'C')) {
          return;
        }
        const newValue = !completionOnly;
        const newIconOb = { ...iconOb };
        newIconOb.completionOnlyToggle.enabled = newValue;
        setIconOb(newIconOb);
        setCompletionOnly(newValue);
        if (onDropdownChange) {
          onDropdownChange({ completionOnly: newValue });
        }
      },
      isChecked: completionOnly,
      /** if the content type is circuits and it's not
       * the user should not have the ability to toggle to make it completion only
       * you can only make a completion only circuit on creation.
       * if the contentType is SAQC (R) and the inputType is "C" for completion only
       * the user can not toggle to update the inputType
       * if the inputType for SAQC is not completion only then the user can toggle the additional
       * options completionOnly for SAQC
       */
      inActive: contentType === 'N' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || content?.type === 'warm_up' || contentType === 'CD' || contentType === 'C' || contentType === 'S' || content?.type === 'progression' || content.type === 'P',
    },
    {
      key: '12',
      label: 'Coach Completion',
      icon: 'coach-completion',
      action: () => {
        if (contentType === 'R' && defaultExOptions.cardioCoach) {
          return;
        }
        const newValue = !coachCompletion;
        const newIconOb = { ...iconOb };
        newIconOb.coachCompletionToggle.enabled = newValue;
        setIconOb(newIconOb);
        setCoachCompletion(newValue);
        if (onDropdownChange) {
          onDropdownChange({ coachCompletion: newValue });
        }
      },
      isChecked: coachCompletion,
      inActive: contentType === 'S' || contentType === 'N' || contentType === 'session_break' || content?.type === 'rest' || content?.type === 'questionnaire' || contentType === 'H' || content?.type === 'video' || contentType === 'CD' || content?.type === 'warm_up' || content?.type === 'progression' || content.type === 'P',
    },
    {
      key: '13a',
      label: 'Assign Pace',
      icon: 'clock',
      action: () => {},
      shortcut: 'pace',
      isChecked: false,
      inActive: contentType !== 'R',
    },
    { divider: true, inActive: contentType === 'S' || contentType === 'CD' || content?.type === 'warm_up' || contentType === 'N' || content?.type === 'video' || content?.type === 'rest' || contentType === 'session_break' || content?.type === 'progression' || content.type === 'P' },
    {
      key: '15',
      label: 'Delete',
      icon: 'delete',
      action: () => {
        if (content.preSave) {
          handleRemovePresaveItemFromRedux();
        } else {
          // Show confirmation modal instead of direct deletion
          const isParentCalendarItem = content.isInherited;
          dispatch(setActiveModal('confirmDeleteModal', {
            assignedId: content.assignedId,
            itemIndex: index,
            isRecurring,
            isParentCalendarItem,
            ...(programType === 'free-form-program' && { dayNumber }),
            ...(isWeekView && effectiveDate && { date: effectiveDate }),
          }));
        }
      },
      color: '#FF0505',
      shortcut: 'Del or Ctrl+D',
      inActive: false,
    },
  ];

  const renderMenuItems = () => menuItems.map((item, renderIndex) => {
    if (item.inActive) return null;

    if (item.divider) return <CustomDivider key={`divider-${renderIndex}`} />;

    return (
      <HoverableMenuItem
        hoverableItem={item}
        content={content}
        isRecurring={isRecurring}
        assignedPace={assignedPace}
        setAssignedPace={setAssignedPace}
        effectiveDate={effectiveDate}
        dayNumber={dayNumber}
        measure={measure}
      />
    );
  });

  return (
    <Dropdown
      trigger={['click']}
      overlay={(
        <MenuWrapper onClick={(e) => e.stopPropagation()}>
          <Menu
            triggerSubMenuAction='click'
            className='custom-rc-menu'
            style={{
              width: 280,
              minHeight: 100,
              /** minus 20 for a bit of padding */
              maxHeight: maxDropdownHeight - 20,
              overflowY: 'auto',
              marginBottom: '0px',
              border: '1px solid #eeeeee',
              background: 'white',
            }}
          >
            <Text fontSize='16px' fontWeight={700} color='#9E9E9E' style={{ paddingLeft: 16, paddingBottom: 8, paddingTop: 8 }}>Actions</Text>
            {renderMenuItems()}
          </Menu>
        </MenuWrapper>
      )}
      visible={localDropdownVisible}
      onVisibleChange={(visible) => {
        setLocalDropdownVisible(visible);
        if (visible) {
          const dropdownIcon = dropdownRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const calculatedMaxHeight = windowHeight - dropdownIcon.top - 25;
          const maxHeight = dropdownIcon.top - 100
          > calculatedMaxHeight ? dropdownIcon.top - 25 : calculatedMaxHeight;
          setMaxDropdownHeight(maxHeight);
        }
      }}
    >
      <DropdownContainer ref={dropdownRef} data-tooltip-id={`${content.assignedId}`}>
        <DropdownIcon>
          <IcomoonReact
            iconSet={iconSet}
            size={18}
            icon='dots'
            className='AdditionalOptionsDots'
            style={{ transform: 'rotate(90deg)' }}
          />
        </DropdownIcon>
        <ReactTooltip
          id={`${content.assignedId}`}
          place='top'
          content='Additional Options'
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 1000,
          }}
        />
      </DropdownContainer>
    </Dropdown>
  );
};

CalendarItemDropdownOptions.defaultProps = {
  isRecurring: '',
  assignedPace: '',
  setAssignedPace: () => {},
  isWeekView: false,
  effectiveDate: null,
  dayNumber: null,
};

CalendarItemDropdownOptions.propTypes = {
  setCoachCompletion: PropTypes.func.isRequired,
  coachCompletion: PropTypes.bool.isRequired,
  setCompletionOnly: PropTypes.func.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isLeftAndRight: PropTypes.bool.isRequired,
  setIsLeftAndRight: PropTypes.func.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  expandId: PropTypes.number.isRequired,
  trackVolumeLoad: PropTypes.bool.isRequired,
  setTrackVolumeLoad: PropTypes.func.isRequired,
  trackRepCount: PropTypes.bool.isRequired,
  setTrackRepCount: PropTypes.func.isRequired,
  forcePrUpdate: PropTypes.bool.isRequired,
  setForcePrUpdate: PropTypes.func.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  setDisablePrUpdate: PropTypes.func.isRequired,
  disablePrUpdate: PropTypes.bool.isRequired,
  iconOb: PropTypes.instanceOf(Object).isRequired,
  setIconOb: PropTypes.func.isRequired,
  handleRemovePresaveItemFromRedux: PropTypes.func.isRequired,
  contentType: PropTypes.string.isRequired,
  localAdditionalInfo: PropTypes.string.isRequired,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  setAssignedPace: PropTypes.func,
  isWeekView: PropTypes.bool,
  onDropdownChange: PropTypes.bool.isRequired,
  effectiveDate: PropTypes.string,
  dayNumber: PropTypes.number,
  measure: PropTypes.func.isRequired,
};

export default withLDConsumer()(CalendarItemDropdownOptions);
